"use client";
import { useEffect, useState } from "react";
import {
  endOfMonth,
  endOfQuarter,
  isBefore,
  isValid,
  parse,
  startOfDay,
  subDays,
} from "date-fns";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import {
  Grid,
  GridContent,
  GridItem,
} from "@aleksundshantu/aunds-utils-react/primitives/grid";

import { EventsGridOptionsType } from "@/types/ModuleOptionsType";
import { fetchEvents } from "@/getData/fetchEvents";
import {
  HighlightCard,
  HighlightCardElement,
} from "@/components/HighlightCard/HighlightCard";
import { mockHighlight } from "../HighlightsGrid/HighlightsGrid.client";
import { ImageType } from "@/types/baseTypes";
import EventGridControls from "./EventGridControls.client";

const parseEventDate = (date: string) => {
  const parsedDate = parse(date, "dd.MM.yyyy", new Date());

  return isValid(parsedDate) ? parsedDate : undefined;
};

const isPastEvent = (event: HighlightCardElement) => {
  const finalEventDate = parseEventDate(event.end_date || event.date);

  return finalEventDate
    ? isBefore(startOfDay(finalEventDate), startOfDay(new Date()))
    : false;
};

interface EventsGridProps {
  eventsGridOptions: {
    box: EventsGridOptionsType["box"];
    grid_options: {
      events_show_range: EventsGridOptionsType["grid_options"]["events_show_range"];
      card_events_grid_options: {
        show_old_events: EventsGridOptionsType["grid_options"]["card_events_grid_options"]["show_old_events"];
      };
    };
  };
  cardHtmlTag: "h2" | "h2-small" | "h3";
  fallbackImage: ImageType | false | undefined;
  excludeIds?: number[];
  limit?: number;
  hideDateRangeFilter?: boolean;
  isRecurringEvent: boolean;
}

const EventGridModuleClient = ({
  eventsGridOptions,
  cardHtmlTag,
  fallbackImage,
  excludeIds,
  limit,
  hideDateRangeFilter,
  isRecurringEvent,
}: EventsGridProps) => {
  const {
    // box: { show_border_radius, show_box_shadow, show_card_box },
    grid_options: {
      events_show_range,
      card_events_grid_options: { show_old_events },
    },
  } = eventsGridOptions;

  const { getLabel, locale, defaultLocale } = useGlobalContext();
  const [showPastEvents, setShowPastEvents] = useState(false);

  // Deep links into a fully past period (?date=yyyyMM) must enable past events,
  // otherwise the fetch short-circuit renders the linked period as empty.
  // Runs once on mount — toggling off pushes a ?date param for the current
  // period and must not re-trigger this.
  useEffect(() => {
    if (!show_old_events) {
      return;
    }

    const dateParam = new URLSearchParams(window.location.search).get("date");

    if (!dateParam) {
      return;
    }

    const parsedDate = parse(
      `${dateParam.substring(0, 6)}01`,
      "yyyyMMdd",
      new Date(),
    );

    if (!isValid(parsedDate)) {
      return;
    }

    const periodEnd =
      events_show_range === "quarter"
        ? endOfQuarter(parsedDate)
        : endOfMonth(parsedDate);

    if (isBefore(periodEnd, startOfDay(new Date()))) {
      setShowPastEvents(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Grid
      postsPerPage={100}
      fetchItems={async ({ filter }) => {
        const dateRangeFilter = filter.dateRangeFilter;
        const today = startOfDay(new Date());
        const requestedStartDate = dateRangeFilter?.startDate;
        const requestedEndDate = dateRangeFilter?.endDate;

        if (
          !showPastEvents &&
          requestedEndDate &&
          isBefore(requestedEndDate, today)
        ) {
          return {
            posts: [],
            totalPosts: 0,
            extraData: { itemsBefore: 0, itemsAfter: 1 },
          };
        }

        const result = await fetchEvents({
          locale,
          defaultLocale,
          startDate:
            !showPastEvents &&
            requestedStartDate &&
            isBefore(requestedStartDate, today)
              ? today
              : requestedStartDate,
          endDate: requestedEndDate,
          excludeIds,
          isRecurringEvent,
        });

        if (!showPastEvents) {
          // The backend's `events_before` also counts events in the real past,
          // which are hidden here. Only upcoming events (today onwards) that
          // precede the requested period may enable the "previous" button.
          const hasUpcomingEventsBefore =
            !!requestedStartDate &&
            isBefore(today, requestedStartDate) &&
            (
              await fetchEvents({
                locale,
                defaultLocale,
                startDate: today,
                endDate: subDays(startOfDay(requestedStartDate), 1),
                excludeIds,
                isRecurringEvent,
              })
            ).posts.length > 0;

          return {
            ...result,
            extraData: {
              ...result.extraData,
              itemsBefore: hasUpcomingEventsBefore ? 1 : 0,
            },
          };
        }

        return result;
      }}
      filterOptions={{
        dateRangeFilter: {
          type: events_show_range,
          showOldEvents: showPastEvents,
        },
      }}
      labels={{ noItems: getLabel("no_events") }}
      renderLoading={{ numberOfItemsWhileLoading: limit ?? 9 }}
    >
      {(events, loading) => {
        const displayedEvents = limit ? events.slice(0, limit) : events;
        return (
          <>
            {!hideDateRangeFilter && (
              <EventGridControls
                allowPastEvents={show_old_events}
                dateRangeType={events_show_range}
                showPastEvents={showPastEvents}
                onShowPastEventsChange={setShowPastEvents}
                showPastEventsToggle={!isRecurringEvent}
              />
            )}
            <GridContent gap="2rem">
              {displayedEvents.map((element, index) => {
                return (
                  <GridItem
                    itemWidth={{
                      mobile: "100%",
                      tablet: "50%",
                      desktop: "50%",
                    }}
                    key={index}
                  >
                    {/* <EventCard
                      skeleton={loading}
                      headlineTag={cardHtmlTag}
                      element={loading ? mockEvent : (element as EventCardType)}
                      show_border_radius={show_border_radius}
                      show_box_shadow={show_box_shadow}
                      moduleType="events-grid"
                      showBox={show_card_box}
                      getLabel={getLabel}
                      locale={locale}
                    /> */}
                    <HighlightCard
                      element={
                        loading
                          ? mockHighlight
                          : (element as HighlightCardElement)
                      }
                      fallbackImage={fallbackImage}
                      headingLevel={cardHtmlTag === "h3" ? 4 : 3}
                      skeleton={loading}
                      type="event"
                      isPast={
                        !loading && isPastEvent(element as HighlightCardElement)
                      }
                    />
                  </GridItem>
                );
              })}
            </GridContent>
          </>
        );
      }}
    </Grid>
  );
};

export default EventGridModuleClient;
